import { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  TextField,
} from "@mui/material";
import { useFeatureFlag } from "@/hooks/useFeatureFlags.tsx";
import SaveProblem from "./SaveProblem.tsx";
import { RECIPIENTS_HINT, NOTIFICATION_RULE_LABELS, RULE_FILTER_LABELS, unlikelyAddress } from "@/utils/alertCopy.ts";
import { firstUnlikelyAddress, groupFieldErrors, parseRecipients } from "@/utils/notificationGroups.ts";
import type { NotificationGroup, NotificationGroupInput } from "@/types";

interface EditorProps {
  /** null when adding a group. */
  readonly group: NotificationGroup | null;
  /** Where the group sits in the list that is sent: a 422's `loc` counts from there. */
  readonly groupIndex: number;
  readonly saving: boolean;
  readonly error: unknown;
  readonly onSave: (group: NotificationGroupInput) => void;
  readonly onCancel: () => void;
  readonly onReload: () => void;
}

function RuleCheckboxes({
  selected,
  error,
  onToggle,
}: {
  readonly selected: readonly string[];
  readonly error: string | undefined;
  readonly onToggle: (rule: string) => void;
}) {
  const labels = useFeatureFlag("vulnWatch") ? NOTIFICATION_RULE_LABELS : RULE_FILTER_LABELS;
  return (
    <FormControl component="fieldset" error={!!error} margin="normal">
      <FormLabel component="legend">Alert types</FormLabel>
      <FormGroup>
        {Object.entries(labels).map(([rule, label]) => (
          <FormControlLabel
            key={rule}
            label={label}
            control={<Checkbox checked={selected.includes(rule)} onChange={() => onToggle(rule)} />}
          />
        ))}
      </FormGroup>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}

function recipientsHelp(fieldError: string | undefined, text: string): string {
  const unlikely = firstUnlikelyAddress(text);
  return fieldError ?? (unlikely ? unlikelyAddress(unlikely) : RECIPIENTS_HINT);
}

export default function NotificationGroupEditor({ group, groupIndex, saving, error, onSave, onCancel, onReload }: EditorProps) {
  const [name, setName] = useState(group?.name ?? "");
  const [recipients, setRecipients] = useState(group?.emails.join("\n") ?? "");
  // Starts from the stored rules, so a rule this dashboard has no checkbox for survives an edit.
  const [rules, setRules] = useState<string[]>(group?.rules ?? []);
  const emails = parseRecipients(recipients);
  const fields = groupFieldErrors(error, groupIndex, emails);

  const toggle = (rule: string) =>
    setRules((current) => (current.includes(rule) ? current.filter((r) => r !== rule) : [...current, rule]));

  return (
    <Dialog open onClose={saving ? undefined : onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{group ? "Edit group" : "Add group"}</DialogTitle>
      <DialogContent>
        <SaveProblem error={error} other={fields.other} onReload={onReload} />
        <TextField
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={!!fields.name}
          helperText={fields.name}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Recipients"
          value={recipients}
          onChange={(event) => setRecipients(event.target.value)}
          error={!!fields.emails}
          helperText={recipientsHelp(fields.emails, recipients)}
          multiline
          minRows={4}
          fullWidth
          margin="normal"
        />
        <RuleCheckboxes selected={rules} error={fields.rules} onToggle={toggle} />
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={() => onSave({ id: group?.id ?? null, name, emails, rules })}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
